import { Col, Row } from 'antd'
import { useFormikContext } from 'formik'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'

import { Icon } from '@iconify/react'
import { useNotification } from 'app/providers'
import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Input } from 'common/components/Input/Input'
import { InputNumber } from 'common/components/InputNumber/InputNumber'
import { Label } from 'common/components/Label/Label'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { CodeSelect } from 'features/Procedure/features/treatments/components/code-select/code-select'
import {
  DiscountHelperText,
  DiscountSelect,
} from 'features/Procedure/features/treatments/components/discount-select/discount-select'
import { isDiscountVisibleForForm } from 'features/Procedure/features/treatments/components/discount-select/discount-utils'
import { useLazyFetchEmrCodeDetailsQuery } from 'features/Procedure/state/procedure.api'
import type {
  DiscountTypeOption,
  EmrCodeItem,
  PatientDefaultDiscount,
} from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { PreviousExtraData } from 'features/Procedure/features/treatments/previous-treatments'
import { DynamicFields, DynamicFieldsSkeleton } from './dynamic-fields'
import type { FormValues } from './schema'
import { applyCptDetails, EMPTY_PROCEDURE_CODE, getValuesOnCptSelect, isAbortError } from './utils'

type Props = {
  isBilled?: boolean
  discountTypes?: ReadonlyArray<DiscountTypeOption>
  patientDefaultDiscount?: PatientDefaultDiscount | null
  isLoadingDynamicFields?: boolean
}

export const ManageEntireProcedure = ({
  isBilled = false,
  discountTypes,
  patientDefaultDiscount,
  isLoadingDynamicFields = false,
}: Props) => {
  const { values, errors, setFieldValue, setValues, handleChange } = useFormikContext<FormValues>()
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()

  const isDiscountVisible = isDiscountVisibleForForm(patientDefaultDiscount, 'treatments')

  const [fetchEmrCodeDetails, { isFetching: isFetchingCodeDetails }] =
    useLazyFetchEmrCodeDetailsQuery()

  // Track the in-flight code-details request so we can cancel it (and its
  // background retry chain) when a newer code is selected or when the dialog
  // closes and this component unmounts. Without aborting, RTK Query keeps
  // retrying the failed request in the background after the dialog is closed.
  const codeDetailsRequestRef = useRef<ReturnType<typeof fetchEmrCodeDetails> | null>(null)

  useEffect(
    () => () => {
      codeDetailsRequestRef.current?.abort()
    },
    [],
  )

  const getErrorMessage = (error: any): string | undefined => {
    if (!error) return undefined
    if (typeof error === 'string') return error
    if (error?.message) return error.message
    return undefined
  }

  const isIcdSingleSelect = Number(values.cpt4_code?.code) === EMPTY_PROCEDURE_CODE

  const handleCptChange = async (newValue: EmrCodeItem[] | EmrCodeItem | null) => {
    const newCpt = Array.isArray(newValue) ? newValue[0] ?? null : newValue

    await setValues((prevValues) => getValuesOnCptSelect(prevValues, newCpt))

    if (!newCpt?.id || !siteId) {
      return
    }

    // Cancel any previous in-flight request before starting a new one.
    codeDetailsRequestRef.current?.abort()
    const request = fetchEmrCodeDetails({ siteId, codeId: newCpt.id })
    codeDetailsRequestRef.current = request

    try {
      const details = await request.unwrap()
      await setValues((prevValues) => applyCptDetails(prevValues, details))
    } catch (error: unknown) {
      // A cancelled request (newer selection or dialog closed) is not a failure.
      if (isAbortError(error)) {
        return
      }
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: 'Failed to load procedure details',
      })
    }
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <CodeSelect
          required
          label='CPT'
          codeType={ProcedureTypeCode.CPT4}
          size='large'
          value={values.cpt4_code as EmrCodeItem | null}
          onChange={(newValue) => {
            void handleCptChange(newValue)
          }}
          placeholder='Search CPT code'
          disabled={isBilled || isFetchingCodeDetails}
          error={getErrorMessage(errors.cpt4_code)}
        />
      </Col>
      <Col xs={24}>
        <CodeSelect
          label='ICD'
          codeType={ProcedureTypeCode.ICD10}
          mode={isIcdSingleSelect ? undefined : 'multiple'}
          size='large'
          value={
            isIcdSingleSelect
              ? ((values.icd10_codes?.[0] ?? null) as EmrCodeItem | null)
              : (values.icd10_codes as EmrCodeItem[])
          }
          onChange={(newValue) =>
            setFieldValue('icd10_codes', isIcdSingleSelect ? [newValue] : newValue)
          }
          placeholder='Search ICD codes'
          disabled={isBilled || isFetchingCodeDetails}
          error={getErrorMessage(errors.icd10_codes)}
        />
      </Col>
      <Col xs={4}>
        <Label label='Units' />
        <Input
          value={values.procedure_units}
          name='procedure_units'
          onChange={handleChange}
          placeholder='Units'
          disabled={isBilled}
          error={errors.procedure_units as string | undefined}
        />
      </Col>
      <Col xs={6}>
        <Label label='Quantity' />
        <InputNumber
          value={values.units}
          name='units'
          onChange={(stringValue) => setFieldValue('units', stringValue)}
          placeholder='Quantity'
          disabled={isBilled}
          status={errors.units ? 'error' : ''}
        />
        <ErrorLabel error={errors.units} />
      </Col>
      {isDiscountVisible && (
        <Col xs={8}>
          <DiscountSelect
            value={values.discount_type_id ?? null}
            onChange={(optionId) => setFieldValue('discount_type_id', optionId)}
            formContext='treatments'
            discountTypes={discountTypes}
            patientDefaultDiscount={patientDefaultDiscount}
            disabledByBilling={isBilled}
          />
        </Col>
      )}
      <Col xs={6}>
        <Input
          label='Price'
          required
          value={values.price}
          name='price'
          onChange={handleChange}
          placeholder='Price'
          disabled={isBilled}
          prefix={<Icon style={{ marginLeft: 6 }} icon='si:dollar-fill' width={16} height={16} />}
          error={errors.price as string | undefined}
        />
      </Col>
      {isDiscountVisible && (
        <Col xs={24}>
          <DiscountHelperText patientDefaultDiscount={patientDefaultDiscount} />
        </Col>
      )}
      {isLoadingDynamicFields || isFetchingCodeDetails ? (
        <DynamicFieldsSkeleton />
      ) : (
        <DynamicFields disabled={isBilled} />
      )}
      <Col xs={24}>
        <PreviousExtraData disabled={isBilled} />
      </Col>
    </Row>
  )
}
